import { useRef, useState } from 'react';
import {
    ApplicantContainer,
    CoApplicantContainer,
    DisclosureDataStore,
    DisclosuresContainer,
    JobDetailsContainer,
    ReviewContainer,
    DynamicDisclosuresContainer,
    ApplicationDataStore,
    PrequalsDisclosuresContainer,
    WellsFargoDisclosure,
    ImperativeForm,
} from '@servicetitan/financing-application-shared';
import { Flex } from '@servicetitan/anvil2';
import {
    WorkflowStore,
    useProviderConfig,
    SetupDataStore,
} from '@servicetitan/financing-workflow-shared';
import { isActivePreApprovalSubmission } from '@servicetitan/financing-utils-shared';
import { applicationPanel } from './ApplicationWorkflow.module.scss';
import { StepperLayout } from '../layout';
import { ApplicationButtons } from './ApplicationButtons';
import { FooterDisclosures } from './FooterDisclosures';

export function ApplicationWithCoApplicationWorkflow() {
    const {
        hasDynamicDisclosures,
        hasReview,
        continueToHardAppAfterSoft,
        showCoApplicant = true,
        hasInlineDisclosures = false,
    } = useProviderConfig();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { disclosureState } = DisclosureDataStore.useStore();
    const { agreedToDisclosures } = disclosureState;
    const [agreedToDynamicDisclosures, setAgreedToDynamicDisclosures] = useState(false);
    const { isEditing } = ApplicationDataStore.useStore();
    const formRef = useRef<ImperativeForm>(null);

    // If the applicant is pre-approved, we skip straight to the hard app.
    // So on the disclosure page we should show the hard app disclosures, not soft app disclosures
    // that the embedded GreenSky config defaults to.
    const showPrequalDisclosures =
        continueToHardAppAfterSoft && !isActivePreApprovalSubmission(setupData);

    const steps = [
        {
            name: 'applicant',
            label: 'Applicant',
            panelClassName: applicationPanel,
            panelComponent: <ApplicantContainer formRef={formRef} />,
            buttonsComponent: <ApplicationButtons formRef={formRef} showBackToPlanSelection />,
        },
        {
            name: 'job-details',
            label: 'Job details',
            panelClassName: applicationPanel,
            panelComponent: hasInlineDisclosures ? (
                <Flex direction="column" gap="6">
                    <JobDetailsContainer formRef={formRef} />
                    <WellsFargoDisclosure />
                </Flex>
            ) : (
                <JobDetailsContainer formRef={formRef} />
            ),
            ...(hasInlineDisclosures && {
                buttonsComponent: (
                    <ApplicationButtons
                        formRef={formRef}
                        isNextButtonDisabled={!agreedToDisclosures}
                    />
                ),
            }),
        },
    ];

    if (showCoApplicant) {
        steps.push({
            name: 'co-applicant',
            label: 'Co-applicant',
            panelClassName: applicationPanel,
            panelComponent: <CoApplicantContainer formRef={formRef} />,
        });
    }

    if (!hasInlineDisclosures) {
        if (!hasDynamicDisclosures) {
            steps.push({
                name: 'disclosures',
                label: 'Disclosures',
                panelClassName: applicationPanel,
                panelComponent: showPrequalDisclosures ? (
                    <PrequalsDisclosuresContainer />
                ) : (
                    <DisclosuresContainer />
                ),
                buttonsComponent: (
                    <ApplicationButtons
                        formRef={formRef}
                        isNextButtonDisabled={!agreedToDisclosures}
                    />
                ),
            });
        } else {
            steps.push({
                name: 'disclosures',
                label: 'Disclosures',
                panelClassName: applicationPanel,
                panelComponent: (
                    <DynamicDisclosuresContainer
                        onChange={canContinue => {
                            setAgreedToDynamicDisclosures(canContinue);
                        }}
                    />
                ),
                buttonsComponent: (
                    <ApplicationButtons
                        formRef={formRef}
                        isNextButtonDisabled={!agreedToDynamicDisclosures}
                    />
                ),
            });
        }
    }
    if (hasReview) {
        steps.push({
            name: 'review',
            label: 'Review',
            panelClassName: applicationPanel,
            panelComponent: <ReviewContainer />,
            buttonsComponent: (
                <ApplicationButtons formRef={formRef} isNextButtonDisabled={isEditing} />
            ),
        });
    }

    return (
        <StepperLayout
            onComplete={() => setWorkflowStage('reviewing')}
            buttonsComponent={<ApplicationButtons formRef={formRef} />}
            footerComponent={<FooterDisclosures />}
            steps={steps}
        />
    );
}
